<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="mb-15px"
      title="会员线等级：每档独立一级/二级比例；升级条件可配。改比例只影响新单。保存条件后不会自动全员重算，需在「用户等级」点重算。"
    />
    <div class="mb-15px">
      <el-button type="primary" v-hasPermi="['bosscircle:dist-rank:update']" @click="openEdit()">新增等级</el-button>
      <el-button @click="load">刷新</el-button>
    </div>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="排序" prop="sort" width="80" />
      <el-table-column label="编码" prop="code" width="90" />
      <el-table-column label="名称" prop="name" width="120" />
      <el-table-column label="一级%" prop="l1Rate" width="90" />
      <el-table-column label="二级%" prop="l2Rate" width="90" />
      <el-table-column label="启用" width="80">
        <template #default="{ row }">
          <el-tag :type="row.enabled ? 'success' : 'info'">{{ row.enabled ? '是' : '否' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="升级条件" min-width="260">
        <template #default="{ row }">
          <div v-if="row.conditions?.length">
            <div v-for="(c, idx) in row.conditions" :key="idx" class="text-13px">
              {{ metricLabel(c) }} ≥ {{ c.threshold }}
            </div>
          </div>
          <span v-else class="text-gray-400">默认档 / 无条件</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" v-hasPermi="['bosscircle:dist-rank:update']" @click="openEdit(row)">
            编辑
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <el-drawer v-model="drawer" :title="form.id ? '编辑等级' : '新增等级'" size="520px">
    <el-form :model="form" label-width="100px">
      <el-form-item label="编码" required>
        <el-input v-model="form.code" placeholder="如 V1" :disabled="!!form.id" />
      </el-form-item>
      <el-form-item label="名称" required>
        <el-input v-model="form.name" />
      </el-form-item>
      <el-form-item label="排序" required>
        <el-input-number v-model="form.sort" :min="1" :max="999" />
      </el-form-item>
      <el-form-item label="一级%" required>
        <el-input-number v-model="form.l1Rate" :min="0" :max="100" />
      </el-form-item>
      <el-form-item label="二级%" required>
        <el-input-number v-model="form.l2Rate" :min="0" :max="100" />
      </el-form-item>
      <el-form-item label="启用">
        <el-switch v-model="form.enabled" />
      </el-form-item>
      <el-form-item label="备注">
        <el-input v-model="form.remark" type="textarea" :rows="2" />
      </el-form-item>
      <el-divider>升级条件（AND）</el-divider>
      <div v-for="(c, idx) in form.conditions" :key="idx" class="cond-row">
        <el-select v-model="c.metric" class="!w-160px" placeholder="指标">
          <el-option label="一级好友" value="L1_COUNT" />
          <el-option label="二级好友" value="L2_COUNT" />
          <el-option label="团队人数" value="TEAM_COUNT" />
          <el-option label="累计佣金(分)" value="SELF_COMMISSION" />
          <el-option label="直推某等级人数" value="L1_RANK_COUNT" />
        </el-select>
        <el-input-number v-model="c.threshold" :min="0" class="!w-140px" />
        <el-input
          v-if="c.metric === 'L1_RANK_COUNT'"
          v-model="c.refRankCode"
          placeholder="参照如 V1"
          class="!w-100px"
        />
        <el-button link type="danger" @click="form.conditions.splice(idx, 1)">删</el-button>
      </div>
      <el-button class="mt-10px" @click="addCond">加条件</el-button>
    </el-form>
    <template #footer>
      <el-button @click="drawer = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </template>
  </el-drawer>
</template>

<script setup lang="ts">
import * as Api from '@/api/business/distRank'

defineOptions({ name: 'BusinessDistRank' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const drawer = ref(false)
const list = ref<Api.DistRankVO[]>([])
const form = reactive<Api.DistRankVO>({
  code: '',
  name: '',
  sort: 50,
  l1Rate: 10,
  l2Rate: 5,
  enabled: true,
  conditions: []
})

const metricLabel = (c: Api.DistRankConditionVO) => {
  const map: Record<string, string> = {
    L1_COUNT: '一级好友',
    L2_COUNT: '二级好友',
    TEAM_COUNT: '团队人数',
    SELF_COMMISSION: '累计佣金(分)',
    L1_RANK_COUNT: `直推${c.refRankCode || ''}及以上`
  }
  return map[c.metric] || c.metric
}

const resetForm = () => {
  Object.assign(form, {
    id: undefined,
    code: '',
    name: '',
    sort: 50,
    l1Rate: 10,
    l2Rate: 5,
    enabled: true,
    remark: '',
    conditions: []
  })
}

const addCond = () => {
  form.conditions = form.conditions || []
  form.conditions.push({ metric: 'L1_COUNT', op: 'GTE', threshold: 1, enabled: true })
}

const openEdit = async (row?: Api.DistRankVO) => {
  resetForm()
  if (row?.id) {
    const detail = await Api.getDistRank(row.id)
    Object.assign(form, detail)
    form.conditions = detail.conditions || []
  }
  drawer.value = true
}

const load = async () => {
  loading.value = true
  try {
    list.value = (await Api.getDistRankList('MEMBER')) || []
  } finally {
    loading.value = false
  }
}

const save = async () => {
  if (!form.code || !form.name) {
    message.warning('请填写编码和名称')
    return
  }
  saving.value = true
  try {
    if (form.id) await Api.updateDistRank({ ...form })
    else await Api.createDistRank({ ...form })
    message.success('保存成功')
    drawer.value = false
    await load()
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.cond-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
</style>
